import { Button, TextField } from '@blueprinthq/joy'
import {
  Box,
  FormControl,
  FormErrorMessage,
  HStack,
  Select,
  VStack
} from '@chakra-ui/react'
import { DialogBody, DialogContainer, DialogHeader } from '@components'
import { Field, type FieldProps, Form, Formik } from 'formik'
import React from 'react'
import { useQueryClient } from 'react-query'
import * as Yup from 'yup'
import {
  getClinicControllerGetClinicLocationQueryKey,
  useClinicControllerUpdateClinicLocation
} from '~/clinician-api'
import type { ClinicLocationDto } from '~/clinician-api/models'

export const US_STATES = [
  { name: 'Alabama', abbreviation: 'AL' },
  { name: 'Alaska', abbreviation: 'AK' },
  { name: 'Arizona', abbreviation: 'AZ' },
  { name: 'Arkansas', abbreviation: 'AR' },
  { name: 'California', abbreviation: 'CA' },
  { name: 'Colorado', abbreviation: 'CO' },
  { name: 'Connecticut', abbreviation: 'CT' },
  { name: 'Delaware', abbreviation: 'DE' },
  { name: 'Florida', abbreviation: 'FL' },
  { name: 'Georgia', abbreviation: 'GA' },
  { name: 'Hawaii', abbreviation: 'HI' },
  { name: 'Idaho', abbreviation: 'ID' },
  { name: 'Illinois', abbreviation: 'IL' },
  { name: 'Indiana', abbreviation: 'IN' },
  { name: 'Iowa', abbreviation: 'IA' },
  { name: 'Kansas', abbreviation: 'KS' },
  { name: 'Kentucky', abbreviation: 'KY' },
  { name: 'Louisiana', abbreviation: 'LA' },
  { name: 'Maine', abbreviation: 'ME' },
  { name: 'Maryland', abbreviation: 'MD' },
  { name: 'Massachusetts', abbreviation: 'MA' },
  { name: 'Michigan', abbreviation: 'MI' },
  { name: 'Minnesota', abbreviation: 'MN' },
  { name: 'Mississippi', abbreviation: 'MS' },
  { name: 'Missouri', abbreviation: 'MO' },
  { name: 'Montana', abbreviation: 'MT' },
  { name: 'Nebraska', abbreviation: 'NE' },
  { name: 'Nevada', abbreviation: 'NV' },
  { name: 'New Hampshire', abbreviation: 'NH' },
  { name: 'New Jersey', abbreviation: 'NJ' },
  { name: 'New Mexico', abbreviation: 'NM' },
  { name: 'New York', abbreviation: 'NY' },
  { name: 'North Carolina', abbreviation: 'NC' },
  { name: 'North Dakota', abbreviation: 'ND' },
  { name: 'Ohio', abbreviation: 'OH' },
  { name: 'Oklahoma', abbreviation: 'OK' },
  { name: 'Oregon', abbreviation: 'OR' },
  { name: 'Pennsylvania', abbreviation: 'PA' },
  { name: 'Rhode Island', abbreviation: 'RI' },
  { name: 'South Carolina', abbreviation: 'SC' },
  { name: 'South Dakota', abbreviation: 'SD' },
  { name: 'Tennessee', abbreviation: 'TN' },
  { name: 'Texas', abbreviation: 'TX' },
  { name: 'Utah', abbreviation: 'UT' },
  { name: 'Vermont', abbreviation: 'VT' },
  { name: 'Virginia', abbreviation: 'VA' },
  { name: 'Washington', abbreviation: 'WA' },
  { name: 'West Virginia', abbreviation: 'WV' },
  { name: 'Wisconsin', abbreviation: 'WI' },
  { name: 'Wyoming', abbreviation: 'WY' }
]

const validationSchema = Yup.object().shape({
  officeName: Yup.string().optional(),
  addressLine1: Yup.string().required('Street address is required'),
  addressLine2: Yup.string().optional(),
  city: Yup.string().required('City is required'),
  state: Yup.string().required('State is required'),
  zipCode: Yup.string().required('Zip code is required')
})

export const OfficeLocationModal = ({
  isOpen,
  onClose,
  clinicLocation,
  clinicId
}: {
  isOpen: boolean
  onClose: () => void
  clinicLocation: ClinicLocationDto | undefined
  clinicId: string
}) => {
  const queryClient = useQueryClient()
  const { mutate: updateClinicLocation } =
    useClinicControllerUpdateClinicLocation({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries(
            getClinicControllerGetClinicLocationQueryKey(clinicId)
          )
          onClose()
        }
      }
    })

  const onSubmit = (values: any) => {
    updateClinicLocation({
      clinicId: clinicId,
      data: {
        displayName: values.officeName,
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        city: values.city,
        state: values.state,
        postalCode: values.zipCode
      }
    })
  }

  return (
    <DialogContainer size="lg" isOpen={isOpen} onClose={onClose}>
      <DialogBody>
        <DialogHeader text="Office Location" />

        <Formik
          validationSchema={validationSchema}
          initialValues={{
            officeName: clinicLocation?.displayName || '',
            addressLine1: clinicLocation?.addressLine1 || '',
            addressLine2: clinicLocation?.addressLine2 || '',
            city: clinicLocation?.city || '',
            state: clinicLocation?.state || '',
            zipCode: clinicLocation?.postalCode || ''
          }}
          onSubmit={values => {
            onSubmit(values)
          }}
        >
          {({ errors, touched }) => (
            <Form>
              <VStack alignItems="flex-start" mt="small">
                <Box w="100%" mb="small">
                  <Field name="officeName">
                    {({ field }: FieldProps) => (
                      <TextField
                        {...field}
                        label="Office name"
                        w="100%"
                        isInvalid={!!(errors.officeName && touched.officeName)}
                        errorText={errors.officeName}
                      />
                    )}
                  </Field>
                </Box>
                <VStack spacing="medium">
                  <Field name="addressLine1">
                    {({ field }: FieldProps) => (
                      <TextField
                        {...field}
                        label="Street address"
                        w="100%"
                        isInvalid={
                          !!(errors.addressLine1 && touched.addressLine1)
                        }
                        errorText={errors.addressLine1}
                        isRequired
                      />
                    )}
                  </Field>
                  <Field name="addressLine2">
                    {({ field }: FieldProps) => (
                      <TextField
                        {...field}
                        label="Office number"
                        w="100%"
                        isInvalid={
                          !!(errors.addressLine2 && touched.addressLine2)
                        }
                        errorText={errors.addressLine2}
                      />
                    )}
                  </Field>
                  <HStack>
                    <Field name="city">
                      {({ field }: FieldProps) => (
                        <TextField
                          {...field}
                          label="City"
                          w="100%"
                          isInvalid={!!(errors.city && touched.city)}
                          errorText={errors.city}
                          isRequired
                        />
                      )}
                    </Field>
                    <Field name="state">
                      {({ field }: FieldProps) => (
                        <FormControl
                          isInvalid={!!(errors.state && touched.state)}
                          position="relative"
                        >
                          <Select
                            {...field}
                            w="100%"
                            height="48px"
                            borderColor="pale_gray"
                            isInvalid={!!(errors.state && touched.state)}
                          >
                            <option value="">Select state</option>
                            {US_STATES.map(state => (
                              <option value={state.abbreviation}>
                                {state.name}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage
                            position="absolute"
                            bottom="-20px"
                            left="0"
                          >
                            {errors.state}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    <Field name="zipCode">
                      {({ field }: FieldProps) => (
                        <TextField
                          {...field}
                          label="Zip code"
                          w="100%"
                          isInvalid={!!(errors.zipCode && touched.zipCode)}
                          errorText={errors.zipCode}
                          isRequired
                        />
                      )}
                    </Field>
                  </HStack>
                </VStack>
              </VStack>
              <HStack mt="large">
                <Button
                  variant="outline"
                  onClick={onClose}
                  borderRadius="8px"
                  w="100%"
                >
                  Cancel
                </Button>
                <Button type="submit" borderRadius="8px" w="100%">
                  Save location
                </Button>
              </HStack>
            </Form>
          )}
        </Formik>
      </DialogBody>
    </DialogContainer>
  )
}
